import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type UseDrawerOptions = {
  defaultClosed?: boolean
}

export type UseDrawerReturnValue<
  TContainer extends HTMLElement,
  TContent extends HTMLElement
> = {
  close: () => void
  containerRef: RefObject<TContainer>
  contentRef: RefObject<TContent>
  updateHeight: () => void
}

/** Use the returned updateHeight function to set container height depending on its content. */
export const useDrawer = <TContainer extends HTMLElement, TContent extends HTMLElement>({
  defaultClosed,
}: UseDrawerOptions = {}): UseDrawerReturnValue<TContainer, TContent> => {
  const [closed, setClosed] = useState(defaultClosed)
  const containerRef = useRef<TContainer | null>(null)
  const contentRef = useRef<TContent | null>(null)

  const close = useCallback(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (container && content) {
      container.style.overflow = 'hidden'
      container.style.height = '0px'
      setClosed(true)
    }
  }, [])

  const updateHeight = useCallback(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (container && content) {
      const contentHeight = content.clientHeight

      container.style.overflow = 'hidden'
      container.style.height = `${contentHeight}px`

      setClosed(false)
    }
  }, [])

  useEffect(() => {
    if (defaultClosed) {
      close()
    }
  }, [close, defaultClosed])

  useEffect(() => {
    if (closed === false) {
      window.addEventListener('resize', updateHeight, { passive: true })

      return () => window.removeEventListener('resize', updateHeight)
    }
  }, [closed, updateHeight])

  return useMemo(
    () => ({
      close,
      containerRef,
      contentRef,
      updateHeight,
    }),
    [close, updateHeight]
  )
}
